import { Button } from "@mui/material";
import { createLinkCell } from "../../Logs/constants/logConstants";

/** Per-company progress columns for the selected batch. */
const PIPELINE_RUN_COLUMNS = [
  {
    field: "logs",
    headerName: "Logs",
    width: 70,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) =>
      params.row.log_stream_name ? createLinkCell(params.row.log_stream_name) : null,
  },
  { field: "company_name", headerName: "Company", width: 160 },
  { field: "status_label", headerName: "Status", width: 140 },
  { field: "task_state", headerName: "Task State", width: 120 },
  { field: "exit_code", headerName: "Exit Code", width: 100 },
  { field: "error_count", headerName: "Error Count", width: 110 },
  { field: "retry_count", headerName: "Retry Count", width: 110 },
  { field: "last_updated", headerName: "Last Updated", width: 200 },
];

/**
 * Run progress columns, plus a cancel button on each unfinished company when
 * `onCancelCompany` is given (only while the batch is running).
 *
 * @param {((companyName: string) => void) | null} onCancelCompany
 */
export function getPipelineRunColumns(onCancelCompany) {
  if (!onCancelCompany) {
    return PIPELINE_RUN_COLUMNS;
  }
  return [
    ...PIPELINE_RUN_COLUMNS,
    {
      field: "cancel",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) =>
        TERMINAL_STATUSES.includes(params.row.status) ? null : (
          <Button size="small" color="error" onClick={() => onCancelCompany(params.row.company_name)}>
            Cancel
          </Button>
        ),
    },
  ];
}

/**
 * Readable labels for a company's in-progress status, which the server reports as the
 * pipeline step that just ran. Finished statuses (done, needs_review, …) show as-is.
 */
export const COMPANY_STATUS_LABELS = {
  preflight_node: "checking data",
  trigger_node: "launching task",
  poll_node: "running",
  retry_node: "retrying",
};

/** Previous-runs table: one row per classification-pipeline celery batch. */
export const PIPELINE_HISTORY_COLUMNS = [
  { field: "created_at", headerName: "Started", width: 200 },
  { field: "status", headerName: "Status", width: 120 },
  { field: "company_count", headerName: "Companies", width: 110 },
  { field: "companies", headerName: "Company names", flex: 1, minWidth: 200 },
];

const COMPANY_PREVIEW_LIMIT = 3;

/** Shape GET /get-pipeline-runs rows for the previous-runs DataGrid. */
export function mapPipelineHistoryRuns(rawRuns) {
  return rawRuns.map((run) => {
    const companies = run.companies ?? [];
    const preview = companies.slice(0, COMPANY_PREVIEW_LIMIT).join(", ");
    const extraCount = companies.length - COMPANY_PREVIEW_LIMIT;
    const extra = extraCount > 0 ? ` +${extraCount}` : "";
    return {
      id: run.task_id,
      task_id: run.task_id,
      created_at: run.created_at ?? "",
      status: run.status ?? "",
      company_count: run.company_count ?? companies.length,
      companies: `${preview}${extra}`,
    };
  });
}

/**
 * Form inputs a batch was started with, from its celery log.
 *
 * Runs started before `run_config` was saved fall back to the per-company results of a
 * finished batch, which carry the same inputs. Returns null when neither is there.
 *
 * @param {object} log - Celery task log for a classification-pipeline batch
 * @returns {object|null} The batch's `run_config` shape, or null
 */
export function getRunConfig(log) {
  if (log.run_config) {
    return log.run_config;
  }
  const results = (log.result?.results ?? []).filter(Boolean);
  if (results.length === 0) {
    return null;
  }
  return {
    companies: results.map((result) => result.company_name),
    competitor_mode_companies: results
      .filter((result) => result.competitive_analysis)
      .map((result) => result.company_name),
    platforms: [...new Set(results.flatMap((result) => result.platforms ?? []))].sort(),
    start_date: results[0].start_date,
    end_date: results[0].end_date,
    include_previously_classified: Boolean(results[0].include_previously_classified),
    skip_geocoder: Boolean(log.skip_geocoder),
  };
}

// Mirrors TERMINAL_COMPANY_STATUSES in server/modules/classification_pipeline/pipeline_graph.py
export const TERMINAL_STATUSES = ["done", "needs_review", "timed_out", "error", "cancelled"];

/** Every submitted company has a Mongo row and a finished status — safe to start the report poll. */
export function isReadyForReport(rows, expectedCount) {
  if (rows.length !== expectedCount) {
    return false;
  }
  return rows.every((row) => TERMINAL_STATUSES.includes(row.status));
}

/** Alert text for each terminal poll outcome (see applyCeleryPollOutcomeToAlert). */
export const POLL_ALERT_COPY = {
  success: { severity: "success", headline: "Classification pipeline completed." },
  failed: { severity: "error", headline: "Classification pipeline failed." },
  cancelled: { severity: "warning", headline: "Classification pipeline cancelled." },
  timeout: {
    severity: "warning",
    message:
      "Report generation is taking longer than expected. Check task logs or try again later.",
  },
  fallback: { severity: "error", message: "Could not verify classification pipeline status." },
};
